import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { UpdateUserInput, useUpdateUserMutation } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { updateCachedUser } from './updateCachedUser';

interface UpdateUserOptions {
  shouldShowErrorToast?: boolean;
}

export const useUpdateUser = (options: UpdateUserOptions = {}) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const [
    updateUserMutation,
    { loading: updateUserLoading, error: updateUserError },
  ] = useUpdateUserMutation({
    update: updateCachedUser,
    onError: () => {
      const shouldShowErrorToast = options.shouldShowErrorToast ?? true;
      if (!shouldShowErrorToast) {
        return;
      }
      showErrorNotification({
        contentComponent: 'Failed to update user',
        props: {},
      });
    },
  });

  const updateUser = useCallback(
    async (variablesToUpdate: Partial<Omit<UpdateUserInput, 'tenantID'>>) => {
      await updateUserMutation({
        variables: {
          ...variablesToUpdate,
          tenantID,
          id: variablesToUpdate.id ?? userID,
        },
      });
    },
    [tenantID, updateUserMutation, userID],
  );

  return {
    updateUser,
    loading: updateUserLoading,
    error: updateUserError,
  };
};
